import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { IconAlertSquareRounded, IconChevronLeft, IconEye, IconEyeOff } from '@tabler/icons-react';
import { useDispatch } from 'react-redux';
import { confirmSignIn } from 'aws-amplify/auth';
import { login } from '../../reducers/authSlice';
import { initializeAuthStore } from '../../utils/authUtils';
import PropTypes from 'prop-types';
import PasswordRequirements from '../PasswordRequirements/PasswordRequirements';

// Import your assets
import logoWhite from '../../assets/images/Logo.png';
import { sendToKinesis } from '../../analytics/kinesisTransport';

const NewPasswordSchema = Yup.object().shape({
  newPassword: Yup.string()
    .min(8, 'New Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])/,
      'New Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
    )
    .required('New Password field is required'),
});

function ConfirmLoginNewPassword({ onGoBack }) {
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');

  const handleConfirmation = async (values, { setSubmitting }) => {
    try {
      setErrorMessage('');
      const { isSignedIn } = await confirmSignIn({
        challengeResponse: values.newPassword,
      });

      if (isSignedIn) {
        const authData = await initializeAuthStore();
        sendToKinesis('user_events', 'USER_CONFIRMED', {
          userid: authData.user.userId,
        });

        if (authData) {
          // Update Redux store with the auth data
          dispatch(login(authData));
        } else {
          // Handle case where auth initialization fails
          throw new Error('Failed to initialize authentication');
        }

        // Navigate to landing page
        navigate('/landing');

        sendToKinesis('user_events', 'USER_LOGGED_IN', {
          userid: authData.user.userId,
        });
      }
    } catch (error) {
      switch (error.name) {
        case 'UserNotFoundException':
          setErrorMessage('No account exists with this email');
          break;
        case 'NotAuthorizedException':
          setErrorMessage('Incorrect username or password');
          break;
        case 'UserNotConfirmedException':
          setErrorMessage('Please confirm your account');
          break;
        default:
          setErrorMessage('An unexpected error occurred. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderNewPasswordForm = () => (
    <Formik
      initialValues={{ newPassword: '' }}
      validationSchema={NewPasswordSchema}
      onSubmit={handleConfirmation}
    >
      {({ values, errors, touched, isSubmitting, setFieldValue }) => (
        <Form className="cst-form">
          <h2 className="text-2xl font-bold mb-2 text-center">
            Set Your New Password
          </h2>
          <p className="text-center text-muted-foreground text-sm mb-4">
            For security reasons, you need to update your password before
            proceeding. Please enter a new password below.
          </p>

          <div className="mb-4">
            <label
              htmlFor="newPassword"
              className="block pb-2 text-sm font-semibold"
            >
              New password*
            </label>
            <div className="input-with-icon create-pass">
              <Field
                type={showPassword ? 'text' : 'password'}
                className={`form-control pr-12 ${touched.newPassword && errors.newPassword ? 'border-red-500' : ''}`}
                id="newPassword"
                name="newPassword"
                autoComplete="off"
                placeholder="Enter new password"
              />
              {values.newPassword && (
                <span
                  className="password-toggle-icon"
                  onClick={() => setShowPassword((prev) => !prev)}
                  style={{ cursor: 'pointer' }}
                >
                  {showPassword ? (
                    <IconEyeOff size={20} />
                  ) : (
                    <IconEye size={20} />
                  )}
                </span>
              )}
            </div>
            {values.newPassword && (
              <PasswordRequirements 
                password={values.newPassword} 
                className="mt-3"
              />
            )}
            {touched.newPassword && errors.newPassword && (
              <div className="text-red-500 text-sm mt-1">
                {errors.newPassword}
              </div>
            )}
          </div>

          {errorMessage && (
            <div className="rounded-md p-3 mb-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-800">
              <IconAlertSquareRounded size={20} />
              <span>{errorMessage}</span>
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span 
                  className="spinner-border spinner-border-sm me-2" 
                  role="status" 
                  aria-hidden="true"
                />
                <span>Updating...</span>
              </>
            ) : (
              'Update Password'
            )}
          </button>
        </Form>
      )}
    </Formik>
  );

  return (
    <div className="prelogin-container w-full">
      <div className="flex flex-col lg:flex-row min-h-screen">
        <div className="w-full lg:w-1/2 p-0">
          <div className="prelogin-leftside">
            <div className="container fix-height">
              <img className="logo logo-large" src={logoWhite} alt="logo white" />
              <div className="text">
                <h1 className="tagline-large">
                  Empowering healthcare providers with real-time prescription coverage clarity
                </h1>
              </div>
            </div>
          </div>
        </div>
        <div className="w-full lg:w-1/2 p-0 bg-white">
          <div className="prelogin-rightside">
            <div className="p-5">
              <div className="flex justify-between mb-8">
                <Link to="/" onClick={onGoBack} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors no-underline">
                  <IconChevronLeft size={20} /> Back
                </Link>
              </div>
              {renderNewPasswordForm()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

ConfirmLoginNewPassword.propTypes = {
  onGoBack: PropTypes.func,
};

export default ConfirmLoginNewPassword;
